'use client';

import { useEffect, type RefObject } from 'react';

type Options = {
  wheelMultiplier?: number;
};

const LINE_HEIGHT = 16;
const FALLBACK_PAGE_HEIGHT = 400;

/**
 * Turns a vertical mouse-wheel tick on the container into a horizontal
 * scroll. Trackpad two-finger swipes (which produce `deltaX`) fall through to
 * the native handler so their inertia is preserved. Only intercepts wheel
 * events when the container actually overflows horizontally — otherwise a
 * vertical wheel should keep scrolling the page as usual.
 *
 * Each tick is handed to `scrollBy({ behavior: 'smooth' })` so the browser
 * animates the scroll with its own physics — the same engine that produces
 * the drag/inertia feel, and the one that plays nicely with `scroll-snap`.
 * Custom rAF/lerp animations fight the snap engine and look stepped.
 *
 * Meant for horizontal scroll containers whose scrollbar is hidden. Used by
 * `SideHeaderCardsRow`'s `ScrollRail` and by the redesign
 * `HorizontalScrollableContentCards` — extend it (add options) rather than
 * copying it so the wheel feel stays consistent across the redesign rails.
 */
export function useWheelToHorizontalScroll(
  ref: RefObject<HTMLElement | null>,
  { wheelMultiplier = 1 }: Options = {},
) {
  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const pixelDelta = (event: WheelEvent): number => {
      switch (event.deltaMode) {
        case 1:
          return event.deltaY * LINE_HEIGHT;
        case 2:
          return event.deltaY * (el.clientHeight || FALLBACK_PAGE_HEIGHT);
        default:
          return event.deltaY;
      }
    };

    const handleWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) {
        return;
      }
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        return;
      }
      event.preventDefault();
      el.scrollBy({
        left: pixelDelta(event) * wheelMultiplier,
        behavior: 'smooth',
      });
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [ref, wheelMultiplier]);
}
